/* global axios */
import ApiClient from '../ApiClient';

class NexceoLeadsAPI extends ApiClient {
  constructor() {
    super('nexceo/leads', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(this.url, { params });
  }

  show(leadId) {
    return axios.get(`${this.url}/${leadId}`);
  }

  // Update a lead. Pass stageKey separately so the backend routes it through
  // KanbanTransitionService rather than a silent column write.
  update(leadId, leadData, stageKey = null) {
    const payload = { lead: leadData };
    if (stageKey) payload.stage_key = stageKey;
    return axios.patch(`${this.url}/${leadId}`, payload);
  }

  getActivities(leadId, type = null) {
    const params = type ? { type } : {};
    return axios.get(`${this.url}/${leadId}/activities`, { params });
  }

  // Lead Workspace — Tasks tab (Nexceo::HumanTask, exposed via
  // Nexceo::LeadTasksController).
  getTasks(leadId) {
    return axios.get(`${this.url}/${leadId}/tasks`);
  }

  createTask(leadId, taskData) {
    return axios.post(`${this.url}/${leadId}/tasks`, taskData);
  }

  completeTask(leadId, taskId, { notes = '', outcome = null } = {}) {
    return axios.post(`${this.url}/${leadId}/tasks/${taskId}/complete`, {
      notes,
      outcome,
    });
  }

  cancelTask(leadId, taskId, notes = '') {
    return axios.post(`${this.url}/${leadId}/tasks/${taskId}/cancel`, {
      notes,
    });
  }

  // Lead Workspace — Property Match tab. Surfaces the existing
  // Nexceo::ProjectMatchingService via Nexceo::PropertyMatchesController.
  getPropertyMatches(leadId, limit = 6) {
    return axios.get(`${this.url}/${leadId}/property_matches`, {
      params: { limit },
    });
  }

  moveStage(leadId, stageKey) {
    return axios.post(`${this.url}/${leadId}/move_stage`, {
      stage_key: stageKey,
    });
  }

  assignAgent(leadId, agentId) {
    return axios.post(`${this.url}/${leadId}/assign_agent`, {
      agent_id: agentId,
    });
  }

  markLost(leadId, reason = '') {
    return axios.post(`${this.url}/${leadId}/mark_lost`, { reason });
  }

  markBooked(leadId) {
    return axios.post(`${this.url}/${leadId}/mark_booked`);
  }

  pauseAi(leadId) {
    return axios.post(`${this.url}/${leadId}/pause_ai`);
  }

  resumeAi(leadId) {
    return axios.post(`${this.url}/${leadId}/resume_ai`);
  }

  pauseFollowUps(leadId) {
    return axios.post(`${this.url}/${leadId}/pause_follow_ups`);
  }

  resumeFollowUps(leadId) {
    return axios.post(`${this.url}/${leadId}/resume_follow_ups`);
  }

  verify(leadId, notes = '') {
    return axios.post(`${this.url}/${leadId}/verify`, { notes });
  }

  reject(leadId, notes = '') {
    return axios.post(`${this.url}/${leadId}/reject`, { notes });
  }

  sendTeamAlert(leadId, { force = false } = {}) {
    return axios.post(`${this.url}/${leadId}/send_team_alert`, { force });
  }

  getVerifiedLeads(params = {}) {
    return axios.get(`${this.url}/verified_leads`, { params });
  }

  getAllLeads(params = {}) {
    return axios.get(`${this.url}/all_leads`, { params });
  }

  markJunk(leadId, reason = '') {
    return axios.post(`${this.url}/${leadId}/mark_junk`, { reason });
  }

  restoreLead(leadId) {
    return axios.post(`${this.url}/${leadId}/restore_lead`);
  }

  // Full manual-lead creation: contact + conversation + optional welcome template.
  // markVerified defaults to true — manual leads are human_verified by default.
  createManual(leadData, { markVerified = true, sendWelcome = false } = {}) {
    return axios.post(this.url, {
      lead: leadData,
      manual: true,
      mark_verified: markVerified,
      send_welcome: sendWelcome,
    });
  }

  // Rule-based chat summary. Called by the drawer on open and polled every 30s.
  getConversationSummary(leadId) {
    return axios.get(`${this.url}/${leadId}/conversation_summary`);
  }

  // Manual document sharing (M26.18, Goal 4) — reuses the exact same
  // Nexceo::DocumentShareService the AI uses on the backend, so Timeline/
  // Lead Activity/Kanban/Notification/Memory stay in sync automatically.
  shareDocuments(leadId, documentTypes) {
    return axios.post(`${this.url}/${leadId}/share_documents`, {
      document_types: documentTypes,
    });
  }

  // Lead Workspace — Email quick action. Real send via the existing
  // Nexceo::LeadDocumentMailer (Nexceo::LeadDocumentEmailsController), not
  // a mailto: link.
  emailDocuments(leadId, documentIds) {
    return axios.post(`${this.url}/${leadId}/document_emails`, {
      document_ids: documentIds,
    });
  }

  // Per-document-type Already Shared/Last Delivered/Last Read state (M26.19,
  // Goal 6), keyed by document_type. Fetched when the Share Documents modal
  // opens, so it can offer Resend on documents already sent.
  getDocumentShareStatus(leadId) {
    return axios.get(`${this.url}/${leadId}/document_share_status`);
  }
}

export default new NexceoLeadsAPI();
